import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  DateRange as DateRangeIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import PDFViewer from '../../components/shared/PDFViewer';
import sumasYSaldosService, {
  SumasYSaldosLinea,
  SumasYSaldosResult,
  SumasYSaldosSearchParams,
} from '../../services/sumasYSaldosService';

type RangoFechaPreset =
  | 'mes-actual'
  | 'mes-pasado'
  | 'ult-3-meses'
  | 'ult-6-meses'
  | 'ult-12-meses'
  | 'personalizado';

function toYmdLocal(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

function rangoFromPreset(
  preset: Exclude<RangoFechaPreset, 'personalizado'>,
  ref: Date = new Date()
): { desde: string; hasta: string } {
  const y = ref.getFullYear();
  const m = ref.getMonth();
  switch (preset) {
    case 'mes-actual':
      return { desde: toYmdLocal(new Date(y, m, 1)), hasta: toYmdLocal(new Date(y, m + 1, 0)) };
    case 'mes-pasado':
      return { desde: toYmdLocal(new Date(y, m - 1, 1)), hasta: toYmdLocal(new Date(y, m, 0)) };
    case 'ult-3-meses':
      return { desde: toYmdLocal(new Date(y, m - 2, 1)), hasta: toYmdLocal(new Date(y, m + 1, 0)) };
    case 'ult-6-meses':
      return { desde: toYmdLocal(new Date(y, m - 5, 1)), hasta: toYmdLocal(new Date(y, m + 1, 0)) };
    case 'ult-12-meses':
      return { desde: toYmdLocal(new Date(y, m - 11, 1)), hasta: toYmdLocal(new Date(y, m + 1, 0)) };
  }
}

function fmt(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const PRESET_CHIPS: { id: RangoFechaPreset; label: string }[] = [
  { id: 'mes-actual', label: 'Mes actual' },
  { id: 'mes-pasado', label: 'Mes pasado' },
  { id: 'ult-3-meses', label: 'Últ. 3 meses' },
  { id: 'ult-6-meses', label: 'Últ. 6 meses' },
  { id: 'ult-12-meses', label: 'Últ. 12 meses' },
  { id: 'personalizado', label: 'Personalizado' },
];

function cuentaCol(linea: SumasYSaldosLinea): string {
  if (linea.tipo === 'rubro') return linea.nroRubro != null ? String(linea.nroRubro) : '';
  if (linea.tipo === 'saldoInicial') return '';
  return linea.nroCuenta != null ? String(linea.nroCuenta) : '';
}

const SumasYSaldos: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const inicialMesActual = useMemo(() => rangoFromPreset('mes-actual', new Date()), []);

  const [fechaDesde, setFechaDesde] = useState(inicialMesActual.desde);
  const [fechaHasta, setFechaHasta] = useState(inicialMesActual.hasta);
  const [rangoPreset, setRangoPreset] = useState<RangoFechaPreset>('mes-actual');
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [result, setResult] = useState<SumasYSaldosResult | null>(null);

  const params = useMemo<SumasYSaldosSearchParams>(
    () => ({ fechaDesde, fechaHasta }),
    [fechaDesde, fechaHasta]
  );

  const aplicarPresetRango = (preset: RangoFechaPreset): void => {
    setRangoPreset(preset);
    if (preset === 'personalizado') return;
    const r = rangoFromPreset(preset, new Date());
    setFechaDesde(r.desde);
    setFechaHasta(r.hasta);
  };

  const handleCargar = async (): Promise<void> => {
    if (!fechaDesde || !fechaHasta) {
      enqueueSnackbar('Indicá fecha desde y fecha hasta.', { variant: 'warning' });
      return;
    }
    try {
      setLoading(true);
      const data = await sumasYSaldosService.get(params);
      setResult(data);
      const cuentas = data.lineas.filter((l) => l.tipo === 'cuenta').length;
      enqueueSnackbar(`Informe cargado (${cuentas} cuenta/s).`, { variant: 'success' });
    } catch (e) {
      setResult(null);
      enqueueSnackbar(sumasYSaldosService.parseError(e), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async (): Promise<void> => {
    try {
      const data = await sumasYSaldosService.getExportData(params);
      if (data.lineas.length === 0) {
        enqueueSnackbar('No hay datos para exportar.', { variant: 'info' });
        return;
      }
      const rows = data.lineas.map((l) => ({
        'Cuenta Contable':
          l.tipo === 'rubro' ? l.nroRubro ?? '' : l.tipo === 'cuenta' ? l.nroCuenta ?? '' : '',
        Descripción: l.descripcion,
        Tipo: l.tipo === 'rubro' ? 'Rubro' : l.tipo === 'saldoInicial' ? 'Saldo inicial' : 'Cuenta',
        Debe: l.tipo === 'rubro' ? '' : l.debe,
        Haber: l.tipo === 'rubro' ? '' : l.haber,
        'Saldo Deudor': l.tipo === 'rubro' ? '' : l.saldoDeudor,
        'Saldo Acreedor': l.tipo === 'rubro' ? '' : l.saldoAcreedor,
      }));
      rows.push({
        'Cuenta Contable': '',
        Descripción: 'TOTALES',
        Tipo: '',
        Debe: data.totalDebe,
        Haber: data.totalHaber,
        'Saldo Deudor': data.totalSaldoDeudor,
        'Saldo Acreedor': data.totalSaldoAcreedor,
      });
      const ws = XLSX.utils.json_to_sheet(rows as unknown as Record<string, unknown>[]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sumas y Saldos');
      XLSX.writeFile(wb, `sumas-y-saldos-${fechaDesde}_${fechaHasta}.xlsx`);
    } catch (e) {
      enqueueSnackbar(sumasYSaldosService.parseError(e), { variant: 'error' });
    }
  };

  const handlePdf = async (): Promise<void> => {
    try {
      setPdfLoading(true);
      const blob = await sumasYSaldosService.getPreviewPdfBlob(params);
      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
    } catch (e) {
      enqueueSnackbar(sumasYSaldosService.parseError(e), { variant: 'error' });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleClosePdfPreview = (): void => {
    setPreviewPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/contabilidad/informes')}
                sx={{ textTransform: 'none' }}
              >
                Informes
              </Button>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Sumas y Saldos
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                color="primary"
                startIcon={pdfLoading ? <CircularProgress size={18} color="inherit" /> : <PictureAsPdfIcon />}
                onClick={() => void handlePdf()}
                disabled={pdfLoading}
                sx={{ borderRadius: 2, px: 3, py: 1, textTransform: 'none', fontWeight: 500 }}
              >
                {pdfLoading ? 'Generando PDF…' : 'PDF'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={() => void handleExportExcel()}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                  '&:hover': { boxShadow: '0 4px 8px rgba(245, 158, 11, 0.4)' },
                }}
              >
                Exportar Excel
              </Button>
            </Stack>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                  Período
                </Typography>
                {PRESET_CHIPS.map(({ id, label }) => (
                  <Chip
                    key={id}
                    label={label}
                    size="small"
                    icon={id === 'personalizado' ? <DateRangeIcon /> : undefined}
                    onClick={() => aplicarPresetRango(id)}
                    color={rangoPreset === id ? 'primary' : 'default'}
                    variant={rangoPreset === id ? 'filled' : 'outlined'}
                  />
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                Por cada cuenta se muestra el <strong>Saldo inicial</strong> (antes de la fecha desde) y los
                movimientos del período en Debe/Haber, con el saldo final en Deudor o Acreedor.
              </Alert>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                type="date"
                label="Fecha desde"
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                value={fechaDesde}
                onChange={(e) => {
                  setFechaDesde(e.target.value);
                  setRangoPreset('personalizado');
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                type="date"
                label="Fecha hasta"
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                value={fechaHasta}
                onChange={(e) => {
                  setFechaHasta(e.target.value);
                  setRangoPreset('personalizado');
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                onClick={() => void handleCargar()}
                disabled={loading}
                sx={{ borderRadius: 2, px: 2, py: 1, textTransform: 'none', fontWeight: 500 }}
              >
                Cargar
              </Button>
            </Grid>
          </Grid>

          {!result && <Alert severity="info">Configurá filtros y pulsá Cargar.</Alert>}

          {result && (
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Cuenta Contable</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="right">Debe</TableCell>
                    <TableCell align="right">Haber</TableCell>
                    <TableCell align="right">Saldo Deudor</TableCell>
                    <TableCell align="right">Saldo Acreedor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.lineas.map((l, idx) => {
                    const isRubro = l.tipo === 'rubro';
                    const isSaldoIni = l.tipo === 'saldoInicial';
                    return (
                      <TableRow
                        key={`${l.tipo}-${l.nroRubro ?? ''}-${l.nroCuenta ?? ''}-${idx}`}
                        sx={{
                          bgcolor: isRubro ? 'rgba(0, 0, 0, 0.06)' : undefined,
                          '& td': {
                            fontWeight: isRubro || l.tipo === 'cuenta' ? 600 : 400,
                            fontStyle: isRubro ? 'italic' : 'normal',
                            pl: isSaldoIni ? 4 : 2,
                          },
                        }}
                      >
                        <TableCell sx={{ pl: `${8 + l.nivel * 12}px !important` }}>{cuentaCol(l)}</TableCell>
                        <TableCell>{l.descripcion}</TableCell>
                        <TableCell align="right">{isRubro ? '' : fmt(l.debe)}</TableCell>
                        <TableCell align="right">{isRubro ? '' : fmt(l.haber)}</TableCell>
                        <TableCell align="right">{isRubro ? '' : fmt(l.saldoDeudor)}</TableCell>
                        <TableCell align="right">{isRubro ? '' : fmt(l.saldoAcreedor)}</TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)' }}>
                    <TableCell />
                    <TableCell sx={{ fontWeight: 700 }}>TOTALES</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {fmt(result.totalDebe)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {fmt(result.totalHaber)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {fmt(result.totalSaldoDeudor)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {fmt(result.totalSaldoAcreedor)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {previewPdfUrl ? (
        <PDFViewer
          pdfUrl={previewPdfUrl}
          onClose={handleClosePdfPreview}
          title="Sumas y Saldos"
          documentId="informe-sumas-y-saldos"
          documentType="informeCliente"
          downloadFileName={`sumas-y-saldos-${fechaDesde}_${fechaHasta}.pdf`}
        />
      ) : null}
    </Box>
  );
};

export default SumasYSaldos;
